import React, { useEffect, useState } from "react";
import { Loader2, Stethoscope, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

import { Switch } from "@/components/ui/switch";
import { listAdminServices, toggleService } from "@/lib/api";

export default function ServicesPanel({ onSessionExpired }) {
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [savingId, setSavingId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const data = await listAdminServices();
      setServices(data || []);
    } catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); return; }
      toast.error("Failed to load services.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const onToggle = async (svc, next) => {
    setSavingId(svc.id);
    // optimistic update
    setServices((s) => s.map((x) => (x.id === svc.id ? { ...x, available: next } : x)));
    try {
      await toggleService(svc.id, next);
      toast.success(`${svc.label} is now ${next ? "Available" : "Unavailable"}.`);
    } catch (err) {
      // revert
      setServices((s) => s.map((x) => (x.id === svc.id ? { ...x, available: !next } : x)));
      toast.error(err?.response?.data?.detail || "Failed to update.");
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "#0B2545" }} /></div>;
  }

  const availCount = services.filter((s) => s.available).length;

  return (
    <div className="space-y-6" data-testid="services-panel">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-medium mb-1" style={{ color: "#7A5E12" }}>
          Services
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0B2545" }}>
          Services
        </h1>
        <p className="mt-1 text-sm" style={{ color: "#64748B" }}>
          Toggle availability — services marked unavailable are greyed out and
          can&apos;t be selected on the public request form.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total services" value={services.length} />
        <StatCard label="Available" value={availCount} accent="#0F6D5D" />
        <StatCard label="Unavailable" value={services.length - availCount} accent="#9B1C1C" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="services-grid">
        {services.map((s) => (
          <div
            key={s.id}
            className="rounded-2xl border bg-white p-5 transition-opacity"
            style={{ borderColor: s.available ? "#E2E8F0" : "#FECACA", opacity: s.available ? 1 : 0.85 }}
            data-testid={`service-card-${s.id}`}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3 min-w-0">
                <span
                  className="grid place-items-center h-11 w-11 rounded-xl shrink-0"
                  style={{
                    background: s.available ? "rgba(212,175,55,0.18)" : "#F1F5F9",
                    color: s.available ? "#7A5E12" : "#94A3B8",
                  }}
                >
                  <Stethoscope className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <div className="font-display text-base font-semibold truncate" style={{ color: "#0B2545" }}>{s.label}</div>
                  <div className="text-xs mt-0.5" style={{ color: "#64748B" }}>{s.description}</div>
                </div>
              </div>
              <Switch
                checked={s.available}
                disabled={savingId === s.id}
                onCheckedChange={(v) => onToggle(s, v)}
                data-testid={`service-toggle-${s.id}`}
              />
            </div>
            <div className="flex items-center gap-1.5 text-xs font-medium"
                 style={{ color: s.available ? "#0F6D5D" : "#9B1C1C" }}>
              {s.available
                ? <><Eye className="h-3.5 w-3.5" /> Visible & selectable on public form</>
                : <><EyeOff className="h-3.5 w-3.5" /> Greyed out — &quot;Temporarily unavailable&quot;</>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const StatCard = ({ label, value, accent = "#0B2545" }) => (
  <div className="rounded-xl border bg-white p-4" style={{ borderColor: "#E2E8F0" }}>
    <div className="text-[10px] uppercase tracking-[0.18em] font-medium mb-1" style={{ color: "#64748B" }}>{label}</div>
    <div className="font-display text-2xl font-bold" style={{ color: accent }}>{value}</div>
  </div>
);
